import { useEffect, useRef, useState } from "react";
import { getWebApp } from "../utils/platform";

// edit — правка присланного фото; create — генерация с нуля (text-to-image)
const EXAMPLES = {
  edit: [
    "Сделай меня в стиле Pixar…",
    "Убери фон, оставь только меня…",
    "Помолоди на 15 лет…",
    "Поставь меня у Эйфелевой башни…",
    "Сделай ремонт в этой комнате…",
  ],
  create: [
    "Кот-космонавт на Марсе…",
    "Уютный домик в заснеженных горах…",
    "Неоновый город под дождём…",
    "Логотип для кофейни с совой…",
    "Дракон над ночным Токио…",
  ],
};
const LABELS = {
  edit: "ОПИШИТЕ СВОИМИ СЛОВАМИ",
  create: "ОПИШИТЕ ЧТО НАРИСОВАТЬ",
};
const ROTATE_MS = 2400;

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (prompt: string) => void;
  compact?: boolean;
  variant?: "edit" | "create";
}

export function CustomPromptHero({
  value,
  onChange,
  onSubmit,
  compact = false,
  variant = "edit",
}: Props) {
  const examples = EXAMPLES[variant];
  const [phIdx, setPhIdx] = useState(0);
  const [focused, setFocused] = useState(false);
  const [borderFlash, setBorderFlash] = useState(false);
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  const hintShown = !value && !focused;

  // Ротация крутится только пока подсказка видна. Под сфокусированным полем любой
  // ре-рендер сбивает нативное меню «Вставить» на iOS, а пустое поле в фокусе —
  // это ровно тот момент, когда в него вставляют из буфера.
  useEffect(() => {
    if (!hintShown) return;
    const t = setInterval(() => setPhIdx((i) => (i + 1) % examples.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [hintShown, examples.length]);

  // Высота строки фиксирована и одинакова у поля и у подсказки: одна и та же
  // line-height на обоих слоях гарантирует, что текст не прыгает при их смене.
  const rowH = compact ? 28 : 34;
  const fontSize = compact ? 17 : 19;
  const activeIdx = phIdx % examples.length;

  const trimmed = value.trim();
  const enabled = trimmed.length > 0;

  const handleSubmit = () => {
    if (!enabled) {
      setBorderFlash(true);
      setTimeout(() => setBorderFlash(false), 600);
      const wa = getWebApp();
      wa?.HapticFeedback?.notificationOccurred?.("warning");
      fieldRef.current?.focus();
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <div
      className="relative overflow-hidden border bg-sk-card"
      style={{
        margin: compact ? "0 0 16px" : "0 0 22px",
        padding: compact ? "14px 14px" : "18px 16px 16px",
        borderRadius: 22,
        borderColor: borderFlash ? "var(--color-sk-accent)" : "var(--color-sk-line)",
        transition: "border-color 0.25s",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(120% 80% at 100% 0%, rgba(255,92,46,0.10), transparent 55%)",
        }}
      />

      <div
        className="flex items-center gap-2 mb-2.5"
        style={{
          color: "var(--color-sk-accent)",
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 0.3,
          fontFamily: "var(--font-sk-mono)",
        }}
      >
        <SparkIcon size={14} />
        <span>{LABELS[variant]}</span>
      </div>

      <div className="relative" style={{ height: rowH }}>
        <textarea
          ref={fieldRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          rows={1}
          aria-label={LABELS[variant]}
          className="relative w-full bg-transparent outline-none resize-none"
          style={{
            height: rowH,
            padding: 0,
            fontSize,
            lineHeight: `${rowH}px`,
            color: "var(--color-sk-text)",
            fontFamily: "var(--font-sk-ui)",
            zIndex: 2,
          }}
        />
        {/* Подсказки — слоем ПОД прозрачным полем. Слой всегда смонтирован и всегда
            одного размера: поле не должно ни ремаунтиться, ни менять высоту, иначе
            iOS роняет контекстное меню вставки. */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden
          style={{
            opacity: hintShown ? 1 : 0,
            transition: "opacity 0.2s ease",
          }}
        >
          {examples.map((ex, i) => (
            <div
              key={i}
              className="absolute inset-0"
              style={{
                color: "var(--color-sk-text-mute)",
                fontSize,
                lineHeight: `${rowH}px`,
                fontFamily: "var(--font-sk-ui)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                opacity: i === activeIdx ? 1 : 0,
                transition: "opacity 0.45s ease",
              }}
            >
              {ex}
            </div>
          ))}
        </div>
      </div>

      {!compact && (
        <div className="mt-3.5 flex gap-2.5 items-center">
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 flex items-center justify-center"
            style={{
              height: 44,
              borderRadius: 14,
              background: enabled ? "var(--color-sk-accent)" : "rgba(255,92,46,0.35)",
              color: enabled ? "#0B0B0B" : "rgba(11,11,11,0.55)",
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: 0.1,
              boxShadow: enabled ? "0 6px 18px rgba(255,92,46,0.35)" : "none",
              transition: "all 0.25s",
            }}
          >
            Сделать
          </button>
        </div>
      )}
    </div>
  );
}

function SparkIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path
        d="M8 1.5l1.4 4 4 1.4-4 1.4L8 12.3 6.6 8.3 2.6 6.9l4-1.4L8 1.5z"
        fill="currentColor"
      />
      <path
        d="M13 10l.6 1.6 1.6.6-1.6.6L13 14.5l-.6-1.7-1.6-.6 1.6-.6L13 10z"
        fill="currentColor"
        opacity="0.6"
      />
    </svg>
  );
}
